import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from 'primereact/utils';

export interface OptionsDropdownItem {
  label: string;
  icon?: ReactNode;
  visible?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface OptionsDropdownAnchor {
  top: number;
  left: number;
  buttonTop?: number;
  gap?: number;
}

interface OptionsDropdownProps {
  open: boolean;
  anchor: OptionsDropdownAnchor | null;
  items: OptionsDropdownItem[];
  onClose: () => void;
  minWidth?: number;
}

export const OptionsDropdown: React.FC<OptionsDropdownProps> = ({ open, anchor, items, onClose, minWidth = 180 }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [resolvedTop, setResolvedTop] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      onClose();
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open || !anchor || !menuRef.current) {
      setResolvedTop(null);
      return;
    }
    const menuHeight = menuRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const margin = 8;
    const gap = anchor.gap ?? 8;

    if (anchor.top + menuHeight + margin <= viewportHeight) {
      setResolvedTop(anchor.top);
      return;
    }
    if (typeof anchor.buttonTop === 'number' && anchor.buttonTop - menuHeight - gap >= margin) {
      setResolvedTop(anchor.buttonTop - menuHeight - gap);
      return;
    }
    setResolvedTop(Math.max(margin, viewportHeight - menuHeight - margin));
  }, [open, anchor, items]);

  if (!open || !anchor) return null;

  const visibleItems = items.filter((item) => item.visible !== false);
  if (!visibleItems.length) return null;

  return createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: `${resolvedTop ?? anchor.top}px`,
        left: `${anchor.left}px`,
        minWidth: `${minWidth}px`,
        visibility: resolvedTop === null ? 'hidden' : 'visible',
        zIndex: 1000,
      }}
      className={classNames(
        'border-fletchers-gray-110 overflow-hidden rounded-sm border bg-white text-black shadow-lg',
      )}
      role="menu"
    >
      <ul className="list-none py-1 outline-none">
        {visibleItems.map((item, index) => (
          <li key={`${item.label}-${index}`}>
            <button
              type="button"
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                item.onSelect();
                onClose();
              }}
              className={classNames(
                'flex h-8 w-full cursor-pointer items-center gap-2 px-3 text-left text-[0.875rem] font-semibold text-black select-none',
                'hover:bg-fletchers-gray-50',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
              role="menuitem"
            >
              {item.icon && (
                <span className="flex h-[14px] w-[14px] shrink-0 items-center justify-center [&_i]:!text-[0.8125rem] [&_svg]:h-[14px] [&_svg]:w-[14px]">
                  {item.icon}
                </span>
              )}
              <span>{item.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>,
    document.body,
  );
};
